import { useRecords } from "@latticexyz/stash/react";
import NumberFlow from "@number-flow/react";
import { formatEther, toNumber } from "@permutation-city/common";
import { BadgeDollarSignIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { PesosBalancesTable } from "../common";
import { useDustClient } from "../hooks/useDustClient";
import { stash } from "../mud/stash";
import { useSyncStatus } from "../mud/useSyncStatus";

export function UserBalance() {
  const { data: dustClient } = useDustClient();
  const userAddress = dustClient?.appContext.userAddress;
  const balances = useRecords({ stash, table: PesosBalancesTable });
  const balance = balances.find((r) => r.account === userAddress)?.value; // TODO: add account filter to sync provider
  const previousBalanceRef = useRef<bigint | undefined>(undefined);
  const syncStatus = useSyncStatus();
  const hasLoadedInitialBalance = syncStatus.isLive;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [balanceChange, setBalanceChange] = useState<
    null | "increase" | "decrease"
  >(null);
  const timeoutRef = useRef<number | null>(null);
  const prevHasLoadedInitialBalance = useRef(false);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio("/assets/chaching.mp3");
      audioRef.current.volume = 0.5;
    }
  }, []);

  useEffect(() => {
    if (!hasLoadedInitialBalance || balance === undefined) return;
    if (!prevHasLoadedInitialBalance.current && hasLoadedInitialBalance) {
      prevHasLoadedInitialBalance.current = true;
      previousBalanceRef.current = balance;
      return;
    }

    if (previousBalanceRef.current !== balance) {
      if (balance > (previousBalanceRef.current ?? 0n)) {
        setBalanceChange("increase");
        audioRef.current?.play().catch(console.error);
      } else if (balance < (previousBalanceRef.current ?? 0n)) {
        setBalanceChange("decrease");
        audioRef.current?.play().catch(console.error);
      }
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setBalanceChange(null);
      }, 1000);
    }
    previousBalanceRef.current = balance;
  }, [balance, hasLoadedInitialBalance]);

  return (
    <div className="uppercase pl-2 pr-[1px] flex items-center gap-2 bg-tertiary border border-tertiary border-solid">
      <span>Balance</span>
      <span
        className={`flex items-center gap-2 bg-primary px-1 h-[20px] transition-colors duration-700 ${
          balanceChange === "increase"
            ? "bg-teal-500"
            : balanceChange === "decrease"
              ? "bg-red-500"
              : "bg-primary"
        }`}
      >
        <BadgeDollarSignIcon className="w-3.5 h-3.5" />
        <span>
          <NumberFlow value={toNumber(formatEther(balance ?? 0n, 2))} />
        </span>
      </span>
    </div>
  );
}
